/**
 * aegisDemoGuardrail — DEMO-1-v2.
 *
 * Intercepts governedAegisCall in demo mode.
 * When demoMode active:
 *   1. Hash the call signature (module + decisionType + tenantId).
 *   2. Look up demo_canned_responses collection for matching canned_id.
 *   3. If match → serve canned response instantly, emit demo.aegis.canned_response.served.
 *   4. If no match → fall through to live AEGIS call, log discrepancy in demo_session_logs.
 *
 * Seeded with 6 pre-captured pairs covering Keyston Act 3 flows.
 * Cache is populated from KEYSTON_AEGIS_DRAFTS in keyston-seed.js.
 *
 * This module exports two things:
 *   aegisDemoGuardrail.shouldIntercept(params) — returns bool
 *   aegisDemoGuardrail.serveCanned(cannedId, tenantId) — returns mock AEGIS result
 *   aegisDemoGuardrail.interceptIfDemo(params, callFn) — used by governedAegisCall
 */

import DataService from '../DataService';
import EventBusService from '../EventBusService';
import { DEMO_TENANT_ID } from '../../data/tenantDefaults';

const CANNED_RESPONSES_COLLECTION = 'demo_canned_responses';
const SESSION_LOGS_COLLECTION = 'demo_session_logs';

const DEMO_SESSION_KEY = typeof window !== 'undefined' ? 'kairos_demo_mode_active' : null;

function isDemoModeActive() {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(DEMO_SESSION_KEY) === '1';
}

// In-memory cache to avoid repeated Firestore lookups during a single demo session.
const _cache = new Map();

// Pre-seeded canned responses — keyed by canned_id.
// Values are the mock AEGIS result shapes served to callers during demo.
const SEEDED_CANNED = {
  'keyston-container-where-is-it': {
    canned_id: 'keyston-container-where-is-it',
    decisionType: 'reply_intelligence',
    module: 'cx',
    text: 'Your container KSTN-CTR-001 arrived at APM Terminals Pier 400 in Los Angeles on April 27th. It is currently awaiting terminal release pending chassis availability. Customs status: entry filed. Estimated ground pickup April 30th; estimated delivery to Dallas DC May 1–2.',
    confidence: 0.87,
    sources: ['ctr-kstn-001', 'load-kstn-046'],
    outputSummary: { containerNumber: 'KSTN-CTR-001', status: 'at_port', etaDelivery: '2026-05-01/02' },
  },
  'keyston-bid-recommendation': {
    canned_id: 'keyston-bid-recommendation',
    decisionType: 'bid_award',
    module: 'tms-ltl',
    text: 'Recommend awarding to Saia LTL Freight: composite score 0.85, $264 rate, 3-day transit. Best balance of cost and reliability versus alternatives.',
    confidence: 0.89,
    sources: ['ltlbid-kstn-004', 'carr-kstn-008'],
    outputSummary: { recommendedCarrierId: 'carr-kstn-008', recommendedRate: 264 },
  },
  'keyston-carrier-delay-notice': {
    canned_id: 'keyston-carrier-delay-notice',
    decisionType: 'reply_intelligence',
    module: 'cx',
    text: 'Load KB-044 experienced a weather hold at Memphis hub. Schneider confirmed resumption this morning. Revised ETA: April 29, 5:00 PM ET. $75 freight credit applied.',
    confidence: 0.91,
    sources: ['load-kstn-044', 'carr-kstn-002'],
    outputSummary: { loadId: 'load-kstn-044', revisedEta: '2026-04-29T17:00:00-05:00', creditApplied: 75 },
  },
  'keyston-inventory-query-answer': {
    canned_id: 'keyston-inventory-query-answer',
    decisionType: 'inventory_intelligence',
    module: 'ims',
    text: 'SKU-CARPET-TILE-4200: 155 units available across branches (Dallas 140 + Phoenix 15). Recommended order: 5 cases of 20 (100 units) to meet pack size. 500-unit customer reserve held for your account.',
    confidence: 0.93,
    sources: ['inv-kstn-001'],
    outputSummary: { totalAvailable: 155, recommendedQty: 100, packSize: 20, cases: 5 },
  },
  'keyston-branch-surplus-suggestion': {
    canned_id: 'keyston-branch-surplus-suggestion',
    decisionType: 'inventory_intelligence',
    module: 'ims',
    text: 'Transfer opportunity: 200 YD LVT Plank 710 from Branch A (Houston, 47 days no movement) to Branch C (San Antonio, deficit). Net margin impact: +$1,247. Recommend approval.',
    confidence: 0.88,
    sources: ['inv-kstn-002'],
    outputSummary: { fromBranch: 'b-houston', toBranch: 'b-san-ant', qty: 200, netMarginImpact: 1247 },
  },
  'keyston-tender-confirmation': {
    canned_id: 'keyston-tender-confirmation',
    decisionType: 'tender_confirmation',
    module: 'tms-ltl',
    text: 'Shipment tendered to Old Dominion (ODFL). Pickup April 28, Dallas DC 8–11 AM. Estimated delivery April 30, Detroit. PRO: 5419-22341.',
    confidence: 0.96,
    sources: ['ltlbid-kstn-007', 'carr-kstn-011'],
    outputSummary: { carrierId: 'carr-kstn-011', proNumber: '5419-22341', pickupDate: '2026-04-28', deliveryDate: '2026-04-30' },
  },
};

function _buildMockResult(canned) {
  return {
    decisionId: `dec_demo_${canned.canned_id}_${Date.now().toString(36)}`,
    status: 'complete',
    mocked: false,
    demo_canned: true,
    hitlStatus: 'autonomous',
    requiresApproval: false,
    confidence: canned.confidence,
    text: canned.text,
    outputSummary: canned.outputSummary || {},
    sources: canned.sources || [],
    agentId: 'DEMO-GUARDRAIL',
    module: canned.module,
    decisionType: canned.decisionType,
    enforcerVerdict: { returnType: 'stated_position', reason: 'demo_canned_pass', gateResults: [] },
    kcritVerdict: null,
    finalDisposition: 'completed',
  };
}

async function serveCanned(cannedId, tenantId) {
  // Try memory cache first
  if (_cache.has(cannedId)) {
    return _buildMockResult(_cache.get(cannedId));
  }

  // Try seeded in-memory map
  if (SEEDED_CANNED[cannedId]) {
    _cache.set(cannedId, SEEDED_CANNED[cannedId]);
    return _buildMockResult(SEEDED_CANNED[cannedId]);
  }

  // Try Firestore collection (user may have added custom canned responses)
  const records = await DataService.query(
    CANNED_RESPONSES_COLLECTION,
    [['canned_id', '==', cannedId], ['tenantId', '==', tenantId || DEMO_TENANT_ID]]
  ).catch(() => []);

  if (records.length > 0) {
    _cache.set(cannedId, records[0]);
    return _buildMockResult(records[0]);
  }

  return null;
}

async function interceptIfDemo(params, callFn) {
  if (!isDemoModeActive()) {
    return callFn(params);
  }

  // Build a lookup key from the call signature
  const lookupKey = params._demo_canned_id || [params.module, params.decisionType].join('::');
  const cannedId = _resolveCannedId(params);

  if (cannedId) {
    const result = await serveCanned(cannedId, params.tenantId);
    if (result) {
      await EventBusService.publish({
        eventType: 'demo.aegis.canned_response.served',
        tenantId: DEMO_TENANT_ID,
        sourceModule: 'demo',
        sourceId: 'aegisDemoGuardrail',
        payload: { cannedId, module: params.module, decisionType: params.decisionType, decisionId: result.decisionId },
        actor: { id: 'demo.guardrail', role: 'platform_admin' },
      }).catch(() => null);
      return result;
    }
  }

  // No match — fall through to live call, log discrepancy
  const liveResult = await callFn(params);

  DataService.create(SESSION_LOGS_COLLECTION, {
    tenantId: DEMO_TENANT_ID,
    type: 'canned_cache_miss',
    lookupKey,
    module: params.module,
    decisionType: params.decisionType,
    loggedAt: Date.now(),
    note: 'Live AEGIS call fired during demo — add to canned cache for consistency.',
  }, DEMO_TENANT_ID).catch(() => null);

  return liveResult;
}

function _resolveCannedId(params) {
  if (params._demo_canned_id) return params._demo_canned_id;
  // Map known (module, decisionType) pairs to canned IDs
  const key = `${params.module}::${params.decisionType}`;
  const MAP = {
    'cx::reply_intelligence': 'keyston-container-where-is-it',
    'tms-ltl::bid_award': 'keyston-bid-recommendation',
    'ims::inventory_intelligence': 'keyston-inventory-query-answer',
    'tms-ltl::tender_confirmation': 'keyston-tender-confirmation',
  };
  return MAP[key] || null;
}

function shouldIntercept(params) {
  return isDemoModeActive() && params.tenantId === DEMO_TENANT_ID;
}

export default { shouldIntercept, serveCanned, interceptIfDemo, isDemoModeActive };
